import { useGetLicenseesQuery } from '@/app/api/licenseesApiSlice';
import { Box, IconButton, TextField, Typography } from '@mui/material';
import SelectField from 'app/shared-components/Forms/Selects/SelectField';
import { memo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import PhoneInput from 'app/shared-components/Forms/Inputs/PhoneInput';
import Icon from 'app/shared-components/Icon/Icon';
import { useCopyToClipboard } from '@/app/hooks/useCopyToClipboard';
import { useGetRolesByRankQuery } from '@/app/api/rolesApiSlice';
import { useIsPermissionAvailable } from '@/app/hooks/useIsPermissionAvailable';
import { useRoleCheck } from '@/app/hooks/useRoleCheck';
import AuthRoles from '@/app/auth/authRoles';
import { Role } from 'app/shared-components/Types/Role';

export interface AddUserInformationFormProps {
  userId?: string;
}

const userInformation: { id: string; fieldName: string; required?: boolean }[] = [
  {
    id: 'email',
    fieldName: 'Email',
    required: true,
  },
  {
    id: 'name',
    fieldName: 'Name',
    required: true,
  },
  {
    id: 'title',
    fieldName: 'Title',
  },
  {
    id: 'phone',
    fieldName: 'Phone',
  },
  {
    id: 'apiKey',
    fieldName: 'Api Key',
  },
  {
    id: 'shortToken',
    fieldName: 'Short Token',
  },
  {
    id: 'password',
    fieldName: 'Password',
  },
  {
    id: 'passwordConfirm',
    fieldName: 'Confirm Password',
  },
];

const AddUserInformationForm = ({ userId }: AddUserInformationFormProps) => {
  const {
    control,
    formState: { errors },
  } = useFormContext();

  const copyToClipboard = useCopyToClipboard();
  const { data: roles, isLoading: rolesIsLoading } = useGetRolesByRankQuery();
  const { data: licenseesData, isLoading: licenseesIsLoading } = useGetLicenseesQuery();
  const isSystemUser = useIsPermissionAvailable('SystemAdmin');
  const isAdmin = useRoleCheck(AuthRoles.AllUsersData);
  const isAccountManagerAndCreateNew = useRoleCheck(['Account Manager']) && !userId;
  const canChangeRole = isAdmin || isAccountManagerAndCreateNew;
  const filteredRoles = isAccountManagerAndCreateNew
    ? {
        ...roles,
        items:
          roles?.items.filter(
            (role: Role) => role.name === 'Company Owner' || role.name === 'Referral Partner',
          ) ?? [],
      }
    : roles;
  return (
    <Box className='flex flex-col items-center justify-center w-full py-8'>
      <Typography variant='h4' className='pt-32' component='h4'>
        User Information
      </Typography>
      <SelectField
        name='roleId'
        label='Role'
        data={filteredRoles}
        isLoading={rolesIsLoading}
        required={true}
        disabled={!canChangeRole}
      />
      <SelectField
        name='licenseeId'
        label='Licensee'
        data={licenseesData}
        isLoading={licenseesIsLoading}
        required={true}
      />
      {userInformation.map(({ id, fieldName, required }) => {
        return (
          <Box className='flex flex-row items-center justify-center w-full py-8' key={id}>
            <Controller
              name={id}
              control={control}
              defaultValue=''
              render={({ field }) => {
                const err = errors[id]?.message as string;
                if (id === 'phone') {
                  return (
                    <PhoneInput
                      value={field.value}
                      onChange={field.onChange}
                      error={!!errors[id]}
                      helperText={err}
                      size='small'
                      className='w-full'
                      label={fieldName}
                    />
                  );
                }
                if (id === 'password' || id === 'passwordConfirm') {
                  return (
                    <TextField
                      {...field}
                      label={fieldName}
                      size='small'
                      variant='outlined'
                      className='w-full'
                      disabled={userId !== undefined}
                      error={!!errors[id]}
                      helperText={err}
                      type={'password'}
                      required={!userId}
                    />
                  );
                }
                if (id === 'apiKey') {
                  return (
                    <Box className='flex flex-row items-center justify-center w-full py-8' key={id}>
                      <TextField
                        {...field}
                        label={fieldName}
                        size='small'
                        variant='outlined'
                        className='w-full'
                        disabled={true}
                        error={!!errors[id]}
                        helperText={err}
                      />
                      <IconButton onClick={() => copyToClipboard(field.value)}>
                        <Icon tooltip='Click to copy' name='copy' pointer={true} />
                      </IconButton>
                    </Box>
                  );
                }
                return (
                  <TextField
                    {...field}
                    label={fieldName}
                    size='small'
                    variant='outlined'
                    className='w-full'
                    error={!!errors[id]}
                    helperText={err}
                    required={required}
                    disabled={id === 'shortToken' && !isSystemUser}
                  />
                );
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default memo(AddUserInformationForm);
